<template>
    <DashboardSnippet v-slot="{
        metrics,
        assetsLoading,
    }">
        <div class="p-1">

            <!-- ── Warehouse Header ─────────────────────────────────────── -->
            <div class="row g-3 mb-3">
                <div class="col-12">
                    <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                        <div class="d-flex align-items-center gap-3">
                            <div>
                                <p class="dash-card-label mb-0">Warehouse</p>
                                <h5 class="dash-title mb-1">{{ warehouse.title }}</h5>
                                <span :class="['status-badge', warehouse.deleted_at ? 'status-inactive' : 'status-active']">
                                    {{ warehouse.deleted_at ? 'Inactive' : 'Active' }}
                                </span>
                            </div>
                        </div>
                        <div class="d-flex flex-wrap gap-2">
                            <button class="btn-action btn-action-secondary" @click="scrollToHistory">
                                <i class="fa fa-history me-2"></i>Allocation History
                            </button>
                            <button class="btn-action btn-action-secondary" @click="editWarehouseProfile">
                                <i class="fa fa-pen me-2"></i>Edit Warehouse
                            </button>
                            <button class="btn-action btn-action-primary" @click="allocateToStation">
                                <i class="fa fa-plus me-2"></i>Allocate to Station
                            </button>
                        </div>
                    </div>
                    <hr class="my-3">
                </div>
            </div>

            <!-- ── Inventory Summary ────────────────────────────────────── -->
            <div class="row g-3 mb-4">
                <div class="col-6 col-md-4">
                    <Counter
                        title="Total Inventory"
                        :value="metrics.stocks_count ?? 0"
                        :growth="metrics.total_stocks_growth"
                        icon="fa fa-boxes"
                        color="text-primary"
                        :loading="assetsLoading"
                    />
                </div>
                <div class="col-6 col-md-4">
                    <Counter
                        title="Engine Units"
                        :value="metrics.engines_count ?? 0"
                        :growth="metrics.total_engines_growth"
                        icon="fa fa-cog"
                        color="text-primary"
                        :loading="assetsLoading"
                    />
                </div>
                <div class="col-6 col-md-4">
                    <Counter
                        title="Battery Units"
                        :value="metrics.batteries_count ?? 0"
                        :growth="metrics.total_batteries_growth"
                        icon="fa fa-battery-full"
                        color="text-primary"
                        :loading="assetsLoading"
                    />
                </div>
            </div>

            <!-- ── Stock Table ──────────────────────────────────────────── -->
            <div class="row g-3 mb-4">
                <div class="col-12">
                    <div class="dash-card">
                        <div class="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
                            <div>
                                <h5 class="fw-bold text-dark mb-1">Stock Inventory</h5>
                                <p class="text-muted small mb-0">
                                    All stock items registered in this warehouse
                                </p>
                            </div>
                            <button class="btn-action btn-action-primary" @click="createStock">
                                <i class="fa fa-plus me-2"></i>Create Stock
                            </button>
                        </div>
                        <Datatable :options="datatableOptions" />
                    </div>
                </div>
            </div>

            <!-- ── Allocation History ───────────────────────────────────── -->
            <div id="allocation-history" class="row g-3 mb-4">
                <div class="col-12">
                    <div class="dash-card">
                        <div class="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
                            <div>
                                <h5 class="fw-bold text-dark mb-1">Allocation History</h5>
                                <p class="text-muted small mb-0">
                                    Batches allocated from this warehouse to stations
                                </p>
                            </div>
                        </div>
                        <PerformanceTable
                            title="Asset Performance"
                            :performance-data="{
                                top:   metrics.top_assets   ?? [],
                                least: metrics.least_assets ?? [],
                            }"
                            :enable-date-filter="false"
                        />
                    </div>
                </div>
            </div>

        </div>
    </DashboardSnippet>
</template>

<script lang="ts">
import type Admin from '@/models/Admin'
import Counter from '@/vue/components/Counter.vue'
import Datatable from '@/vue/components/Datatable.vue'
import PerformanceTable from '@/widget/table/PerformanceTable.vue'
import DashboardSnippet from './DashboardSnippet.vue'
import { CellRender } from '@/vue/utils/Datatable'
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'

@Component({
    components: { Counter, DashboardSnippet, Datatable, PerformanceTable },
})
class WarehouseDashboard extends Vue {
    @Prop({ type: Object, required: true })
    readonly warehouse!: Record<string, any>

    @Prop({ type: Object, required: true })
    readonly auth!: Admin

    // ── Datatable ─────────────────────────────────────────────────────────────

    get datatableOptions() {
        return {
            ajax: {
                url:    this.route('stock.ajax.table'),
                method: 'POST',
            },
            buttons: this.buttons,
            columns: this.columns,
        }
    }

    get buttons() {
        return [
            'refresh',
            {
                extend: 'create',
                attr: { href: this.route('engine.page.create') },
            },
            'trashed',
        ]
    }

    get columns() {
        return [
            {
                data:      'serial',
                title:     'Serial Number',
                orderable: true,
            },
            {
                data:      'warehouse.title',
                title:     'Assigned Warehouse',
                orderable: true,
                name:      'warehouse',
            },
            {
                data:      'type',
                title:     'Type',
                orderable: true,
                name:      'type',
            },
            {
                data:     'created_at',
                title:    'Registered',
                render:   CellRender.date(),
                orderable: true,
                name:     'created_at',
            },
        ]
    }

    // ── Actions ───────────────────────────────────────────────────────────────

    scrollToHistory(): void {
        document.getElementById('allocation-history')
            ?.scrollIntoView({ behavior: 'smooth' })
    }

    allocateToStation(): void {
        this.router.visit(this.route('assignment.page.create'))
    }

    editWarehouseProfile(): void {
        this.router.visit(
            this.route('warehouse.page.update', { warehouse: this.warehouse.slug })
        )
    }

    createStock(): void {
        this.router.visit(this.route('stock.page.create'))
    }
}

export default toNative(WarehouseDashboard)
</script>

<style lang="scss" scoped>
.dash-title      { font-size: 1.25rem; font-weight: 700; color: #111827; letter-spacing: -0.3px; }
.dash-card-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; color: #9ca3af; text-transform: uppercase; margin-bottom: 0.4rem; }

.dash-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    transition: box-shadow 0.2s;
    &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
}

.status-badge    { display: inline-block; font-size: 0.7rem; font-weight: 600; padding: 0.2rem 0.55rem; border-radius: 20px; letter-spacing: 0.03em; }
.status-active   { background: #dcfce7; color: #15803d; }
.status-inactive { background: #fee2e2; color: #b91c1c; }

.btn-action { border: none; padding: 0.45rem 1rem; border-radius: 8px; font-size: 0.82rem; font-weight: 500; cursor: pointer; transition: all 0.15s ease; white-space: nowrap; }
.btn-action-primary   { background: #3D5A00; color: #fff; &:hover { background: #4a6e00; } }
.btn-action-secondary { background: #fff; color: #3D5A00; border: 1px solid #e5e7eb; &:hover { background: #f9fafb; } }
</style>